import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, ShieldCheck, Circle, CheckCircle2, XCircle, TrendingUp, ArrowRight } from "lucide-react";
import { Card, Pill } from "../common/Primitives";
import api from "../../lib/api";

const LABEL_TONE = {
  Exceptional: "success",
  Strong: "success",
  Building: "warn",
  Weak: "danger",
};

function CategoryRow({ cat }) {
  const [open, setOpen] = useState(false);
  const pct = cat.max ? Math.round((cat.score * 100) / cat.max) : 0;
  const complete = cat.items.filter((i) => i.complete).length;
  return (
    <div className="border border-navy/10 rounded-lg" data-testid={`ps-cat-${cat.name.replace(/\s+/g, "-").toLowerCase()}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-navy/[0.02] transition-colors"
        data-testid={`ps-cat-toggle-${cat.name.replace(/\s+/g, "-").toLowerCase()}`}
      >
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2">
            <div className="font-heading text-ink text-sm">{cat.name}</div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink-soft">
              {complete}/{cat.items.length}
            </span>
          </div>
          <div className="mt-1.5 h-1 bg-navy/8 rounded overflow-hidden">
            <div
              className={`h-full transition-all ${pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-mono text-ink-soft">{cat.score}/{cat.max}</span>
          {open ? <ChevronUp size={14} className="text-ink-soft" /> : <ChevronDown size={14} className="text-ink-soft" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-navy/10 divide-y divide-navy/5">
          {cat.items.map((it, i) => (
            <div key={i} className="flex items-start gap-3 px-4 py-2.5" data-testid={`ps-item-${cat.name.replace(/\s+/g, "-").toLowerCase()}-${i}`}>
              <div className="pt-0.5">
                {it.complete
                  ? <CheckCircle2 size={14} className="text-emerald-600" />
                  : it.weight >= 5 ? <XCircle size={14} className="text-rose-600" /> : <Circle size={14} className="text-amber-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-ink flex items-center justify-between gap-2 flex-wrap">
                  <span className={it.complete ? "line-through text-ink-soft" : ""}>{it.label}</span>
                  <span className="text-[10px] font-mono text-ink-faint">+{it.weight} pts</span>
                </div>
                {it.hint && !it.complete && (
                  <div className="text-xs text-ink-soft mt-0.5">{it.hint}</div>
                )}
              </div>
              {!it.complete && it.cta_link && (
                <Link
                  to={it.cta_link}
                  className="shrink-0 text-[11px] font-mono uppercase tracking-widest text-[#8A6600] hover:underline flex items-center gap-1"
                  data-testid={`ps-item-cta-${cat.name.replace(/\s+/g, "-").toLowerCase()}-${i}`}
                >
                  Fix <ArrowRight size={11} />
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfileStrengthCard() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/startup/profile-strength")
      .then((r) => setData(r.data))
      .catch(() => { /* silent */ })
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || !data) return null;

  const scoreColor = data.overall_score >= 80 ? "text-emerald-600"
    : data.overall_score >= 50 ? "text-amber-600" : "text-rose-600";
  const ringPct = data.overall_score;

  return (
    <Card testId="profile-strength-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          {/* Radial score ring */}
          <div className="relative w-20 h-20 shrink-0" data-testid="ps-ring">
            <svg viewBox="0 0 100 100" className="w-20 h-20 -rotate-90">
              <circle cx="50" cy="50" r="42" strokeWidth="8" className="stroke-navy/10 fill-none" />
              <circle
                cx="50" cy="50" r="42" strokeWidth="8" fill="none" strokeLinecap="round"
                className={ringPct >= 80 ? "stroke-emerald-500" : ringPct >= 50 ? "stroke-amber-500" : "stroke-rose-500"}
                style={{ strokeDasharray: 263.9, strokeDashoffset: 263.9 - (263.9 * ringPct) / 100, transition: "stroke-dashoffset 600ms" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className={`font-heading text-2xl ${scoreColor}`} data-testid="ps-score">{data.overall_score}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-ink-soft">/100</div>
            </div>
          </div>
          <div className="min-w-0">
            <div className="overline flex items-center gap-1.5"><ShieldCheck size={12} className="text-gold" /> Profile Strength</div>
            <div className="font-heading text-ink text-lg mt-1 flex items-center gap-2 flex-wrap">
              <span data-testid="ps-label">{data.label}</span>
              <Pill tone={LABEL_TONE[data.label] || "muted"}>{data.missing_count} gaps</Pill>
            </div>
            {data.next_action ? (
              <div className="text-sm text-ink-soft mt-1" data-testid="ps-next-action">
                Next best move: <Link to={data.next_action.cta_link} className="text-[#8A6600] font-medium hover:underline">{data.next_action.label} <ArrowRight size={11} className="inline" /></Link>
                <span className="text-ink-faint"> · +{data.next_action.weight} pts</span>
              </div>
            ) : (
              <div className="text-sm text-emerald-700 mt-1 flex items-center gap-1" data-testid="ps-complete">
                <TrendingUp size={12} /> All investor-checkable signals green.
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-xs font-mono uppercase tracking-widest text-[#8A6600] hover:underline flex items-center gap-1 shrink-0"
          data-testid="ps-expand-toggle"
        >
          {expanded ? <>Hide breakdown <ChevronUp size={11} /></> : <>See breakdown <ChevronDown size={11} /></>}
        </button>
      </div>

      {expanded && (
        <div className="mt-5 space-y-2" data-testid="ps-breakdown">
          {data.categories.map((cat) => (<CategoryRow key={cat.name} cat={cat} />))}
        </div>
      )}
    </Card>
  );
}
